<template>
  <div>
    <div class="pb-3" v-html="$t('ARTWORK_UPLOAD.IMAGE_DESC')"></div>

    <artwork-upload
      :artwork="artwork"
      @uploaded="onUploaded"
      @error="$emit('error', 'step1')"
      @video-uploaded="onVideoUploaded"
    />

    <v-container>
      <v-layout row wrap>
        <v-flex xs12 sm12 md9>
          <base-card color="secondary" v-if="selectedImage" height="100%">
            <!-- If selected image is video -->
            <div v-if="selectedImage === artwork.video_id">
              <v-alert v-if="!artwork.is_video_approved" :value="true" color="warning">
                {{ $t("ARTWORK_UPLOAD.NEEDS_ADMIN_APPROVAL") }}
              </v-alert>
              <video-player :videoId="selectedImage" />
              <v-card-actions>
                <v-spacer />
                <tk-btn icon @click="removeVideo" :loading="videoDeleting">
                  <v-icon small>fas fa-trash</v-icon>
                </tk-btn>
              </v-card-actions>
            </div>

            <!-- IF selected item is image -->
            <template v-else>
              <v-img
                :src="selectedImage.url"
                :srcset="selectedImage.srcset"
                contain
              />
              <v-card-actions>
                <!--
              <tk-btn
                flat
                class="text-none"
                :disabled="mainImage && mainImage.id === selectedImage.id"
                @click="setMainImage"
              >Set as main image</tk-btn>
              -->
                <v-spacer />
                <tk-btn
                  icon
                  :disabled="mainImage && mainImage.id === selectedImage.id"
                  @click="removeImage(selectedImage)"
                >
                  <v-icon small>fas fa-trash</v-icon>
                </tk-btn>
              </v-card-actions>
            </template>
          </base-card>
        </v-flex>
        <v-flex xs12 sm12 md3 text-xs-right>
          <v-layout row wrap align-start justify-start>
            <v-flex md12 sm4 xs4 v-for="image in images" :key="image.id">
              <v-img
                :src="image.url"
                :srcset="image.srcset"
                @click="selectedImage = image"
                :class="{
                  selected: selectedImage && selectedImage.id === image.id,
                  'main-img': mainImage && mainImage.id === image.id,
                }"
              />
            </v-flex>
            <v-flex v-if="artwork && artwork.video_id">
              <VideoThumbnail
                :artworkId="artwork.id"
                :videoId="artwork.video_id"
                :class="{ selected: selectedImage === artwork.video_id }"
                isProfile
                @click="selectedImage = artwork.video_id"
              />
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-container>

    <div class="text-xs-right">
      <tk-btn
        :disabled="!mainImage"
        flat
        @click="$router.push({ name: 'profile.artworks' })"
        >{{ $t("COMMON.SAVE_RETURN") }}</tk-btn
      >

      <tk-btn
        v-if="images.length > 1"
        :loading="saving"
        :disabled="!mainImage"
        color="primary"
        @click="$emit('next', 'step1')"
        >{{ $t("COMMON.CONTINUE") }}</tk-btn
      >

      <v-tooltip v-else top dark>
        <template v-slot:activator="{ on }">
          <div v-on="on" class="d-inline-flex">
            <tk-btn
              disabled
              color="primary"
              v-text="$t('COMMON.CONTINUE')"
            ></tk-btn>
          </div>
        </template>
        <span>{{ $t("TOOLTIP.ARTWORK_UPLOAD_FORM.NEEDS_2_IMAGES") }}</span>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
import VideoPlayer from "@/views/Video/VideoPlayer.vue";
import VideoThumbnail from "@/views/Video/VideoThumbnail.vue";
import ArtworkUpload from "./ArtworkUpload.vue";

import { RepositoryFactory } from "@/services/RepositoryFactory";
import { ErrorHandler } from "@/utils/ErrorHandler";
import { ProfileArtwork } from "@/services/artwork/ProfileArtwork";

const artworksRepo = RepositoryFactory.artworks();
const handler = new ErrorHandler();

export default {
  props: {
    artwork: {
      type: ProfileArtwork,
    },
  },

  components: {
    ArtworkUpload,
    VideoPlayer,
    VideoThumbnail,
  },

  data() {
    return {
      selectedImage: null,
      mainImage: null,
      images: [],
      saving: false,
      videoDeleting: false,
    };
  },

  created() {
    this.initImages();
  },

  methods: {
    async removeImage(image) {
      this.saving = true;
      const pos = this.images.findIndex((i) => i.id === image.id);
      this.images.splice(pos, 1);
      this.selectedImage = this.images[0];
      await handler.handle(async () => {
        await artworksRepo.removeArtworkImage(this.artwork.id, image.id);
      });
      this.saving = false;
    },

    onUploaded(res) {
      // this.artwork = res;

      if (res.media.length) {
        this.selectedImage = this.images[0];
        this.$emit("created", res);
        this.$nextTick(() => {
          if (this.artwork.id && !this.$route.params.artworkId) {
            this.$router.replace({
              name: "profile.artworks.edit",
              params: { artworkId: this.artwork.id },
            });
          }
          this.initImages();
        });
      }
    },

    initImages() {
      if (this.artwork && this.artwork.media) {
        this.images = this.artwork.media;
        this.mainImage = this.selectedImage = this.images[0];
      }
    },

    onVideoUploaded(data) {
      this.artwork.video_id = data;
      this.selectedImage = data;
      this.artwork.is_video_approved = false;
    },

    async removeVideo() {
      this.videoDeleting = true;
      await handler.handle(async () => {
        await artworksRepo.deleteVideo(this.artwork.id);
        this.artwork.video_id = null;
        this.selectedImage = this.images[0];
      });
      this.videoDeleting = false;
    },

    /*
    async setMainImage() {
      this.saving = true;
      this.mainImage = this.selectedImage;
      await handler.handle(async () => {
        await artworksRepo.createArtwork(this.artwork.id, 1, false, {
          primary_media_id: this.selectedImage.id,
        });
      });
      this.saving = false;
    },
    */
  },
};
</script>

<style lang="scss" scoped>
.selected {
  border: 2px solid var(--v-primary-base);
}
.main-img {
  &:before {
    content: "Main";
    position: absolute;
    top: 0px;
    right: 0px;
    font-weight: 500;
    background-color: white;
    padding: 5px;
  }
}
</style>
